/**
 * theme.js — Central design configuration for OptionView.
 *
 * Edit values here to change the look of the entire app.
 * Changes take effect on next page load (or hot-reload in dev).
 *
 * bgTexture.type options:
 *   'gradient' — CSS gradient string in .value
 *   'noise'    — SVG/CSS data URI in .value (e.g. an inline SVG noise filter)
 *   'image'    — URL string in .value (e.g. '/textures/grain.png')
 *   null       — no texture, solid colour only
 *
 * headingTexture:
 *   null        — plain text colour (default)
 *   CSS string  — a CSS gradient applied via background-clip:text
 *                 e.g. 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
 */

const theme = {

  // ── FONTS ─────────────────────────────────────────────────────────────────
  // app.sans  → used by tailwind.config.js fontFamily + CSS var
  // blog.sans → null = inherit app font; set a Google Fonts name for a blog-only font
  //             (also uncomment the matching @import line in src/index.css)
  fonts: {
    app:  { sans: 'Inter', mono: 'B612 Mono' },
    blog: { sans: null },
  },

  // ── BRAND COLORS ──────────────────────────────────────────────────────────
  // Full 50-950 palettes for primary and secondary.
  // Dark mode uses lighter shades (300-400) vs light mode (600-700) — already
  // handled by component classes (e.g. dark:text-primary-400). No light/dark split here.
  // To switch brand: replace primary with any 11-shade palette.
  // Example palettes: Tailwind indigo, emerald, rose, violet — just swap the hex values.
  colors: {
    // Cerulean/steel-blue palette built consistently around #016ea9 at shade 600.
    // All shades are the same hue family — hover/active states will always feel cohesive.
    primary: {
      50:  '#f0f9ff',
      100: '#e0f3ff',
      200: '#bae6fd',
      300: '#67c8f6',
      400: '#29b0e8',
      500: '#0292c8',
      600: '#016ea9',
      700: '#015887',
      800: '#014265',
      900: '#012e47',
      950: '#011e30',
    },
    secondary: {
      50:  '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7e22ce',
      800: '#6b21a8',
      900: '#581c87',
      950: '#3b0764',
    },
  },

  // ── APP CONTEXT (all non-blog pages) ──────────────────────────────────────
  app: {
    light: {
    //   bgColor: '#edf0ff',
      bgColor: 'rgba(245, 247, 241, 1)',
      bgTexture: {
        // type:  'gradient',
        type: null,
        // value: [
        // //   'radial-gradient(ellipse at 20% 10%, rgba(199,210,254,0.6) 0%, transparent 50%)',
        // //   'radial-gradient(ellipse at 80% 80%, rgba(216,180,254,0.3) 0%, transparent 50%)',
        //   'radial-gradient(ellipse at 20% 10%, rgba(200, 186, 159, 0.6) 0%, transparent 50%)',
        //   'radial-gradient(ellipse at 80% 80%, rgba(200 ,186, 159, 0.3) 0%, transparent 50%)',
        // ].join(', '),
      },
      textColor:    'rgba(95, 95, 95, 1)',
      surfaceBg:    'rgba(255, 255, 255, 0.9)',   // card / panel fill
      surfaceBorder:'rgba(255, 255, 255, 0.7)',   // card / panel outline
      headingColor:   null,   // null = inherit textColor; set e.g. '#1a1a1a' to control separately
    //   headingTexture: 'linear-gradient(135deg, rgba(245, 247, 241, 1), #8b5cf6 100%)',
      headingTexture: null,
    },
    dark: {
      bgColor:      '#051a26',
      bgTexture: { type: null },
      textColor:    '#f5f5f5',
      surfaceBg:    '#122833',   // card / panel fill  ← edit here to retheme dark cards
      surfaceBorder:'#2b3e47',   // card / panel outline ← edit here to retheme dark borders
      headingColor:   null,
      headingTexture: null,
    },
  },

  // ── BLOG CONTEXT (/blog, /blog/:theme, /blog/:theme/:slug) ─────────────────
  blog: {
    light: {
      bgColor:      'rgba(245, 247, 241, 1)',   // same warm cream as app
      bgTexture: { type: null },
      textColor:    'rgba(95, 95, 95, 1)',
      surfaceBg:    'rgba(255, 255, 255, 0.9)',
      surfaceBorder:'rgba(255, 255, 255, 0.7)',
      headingColor:   null,
      headingTexture: null,
    },
    dark: {
      bgColor:      '#051a26',
      bgTexture: { type: null },
      textColor:    '#f5f5f5',
      surfaceBg:    '#122833',
      surfaceBorder:'#3d5866',
      headingColor:   null,
      headingTexture: null,
    },
  },

  // ── NAV BAR ───────────────────────────────────────────────────────────────
  // Controls the sticky top header and all nav link states.
  nav: {
    light: {
      bg:     'rgba(245, 247, 241, 0.70)',   // warm cream, matches app bg
      border: 'rgba(95, 95, 95, 0.18)',
      brandText:       'rgba(95, 95, 95, 1)',
      linkText:        'rgba(95, 95, 95, 1)',
      linkHoverText:   'var(--color-primary-700)',
      linkHoverBg:     'rgba(255,255,255,0.80)',
      linkActiveBg:    'var(--color-primary-50)',
      linkActiveText:  'var(--color-primary-700)',
      dropdownBg:      '#ffffff',
      dropdownBorder:  'rgba(255,255,255,0.70)',
    //   dropdownBorder:  'rgba(0, 0, 0, 0.7)',
      dropdownLinkText:'#404040',                 // neutral-700
      iconText:        '#737373',                 // dark-mode toggle + hamburger
      iconHoverText:   '#171717',
      iconHoverBg:     'rgba(255,255,255,0.80)',
      mobileBg:        'rgba(245, 247, 241, 0.96)',
      mobileBorder:    'rgba(95, 95, 95, 0.15)',
    },
    dark: {
      bg:     'rgba(5, 26, 38, 0.80)',
      border: 'rgba(200, 200, 200, 0.12)',
      brandText:       '#f5f5f5',
      linkText:        '#c8d8e0',
      linkHoverText:   '#f5f5f5',
      linkHoverBg:     'rgba(255,255,255,0.08)',
      linkActiveBg:    'rgba(255,255,255,0.10)',
      linkActiveText:  '#f5f5f5',
      dropdownBg:      '#3D5866',
      dropdownBorder:  'rgba(200,200,200,0.10)',
      dropdownLinkText:'#c8d8e0',
      iconText:        '#90aab8',
      iconHoverText:   '#f5f5f5',
      iconHoverBg:     'rgba(255,255,255,0.08)',
      mobileBg:        'rgba(5, 26, 38, 0.96)',
      mobileBorder:    'rgba(200,200,200,0.10)',
    },
  },

  // ── SHADOWS ───────────────────────────────────────────────────────────────
  // Controls all shadow-clay-* Tailwind classes via CSS vars.
  // Set any value to 'none' to go flat (e.g. when switching away from claymorphism).
  // clay/clayHover/clayBtn/clayBtnPress → light-mode card, hover, button, press states
  // clayDark/clayDarkHover/clayBtnDark  → dark-mode equivalents (used via dark:shadow-clay-dark etc.)
  shadows: {
    clay:         'none',
    clayHover:    'none',
    clayBtn:      'none',
    clayBtnPress: 'none',
    clayDark:         'none',
    clayDarkHover:    'none',
    clayBtnDark:      'none',
  },

  // ── PROSE COLORS (Tailwind Typography --tw-prose-* vars) ──────────────────
  // These map 1-to-1 to the CSS vars that @tailwindcss/typography exposes.
  // camelCase keys → kebab-case var names: quoteBorders → --tw-prose-quote-borders
  prose: {
    light: {
      body:         'rgba(95, 95, 95, 1)',   // paragraph text
      headings:     'rgba(95, 95, 95, 1)',
      lead:         'rgba(95, 95, 95, 1)',
      links:        'var(--color-primary-600)',
      bold:         'rgba(95, 95, 95, 1)',
      counters:     '#6b7280',
      bullets:      '#9ca3af',
      hr:           '#e5e7eb',
      quotes:       '#111827',
      quoteBorders: 'var(--color-primary-400)',
      captions:     '#6b7280',
      code:         '#111827',
      preCode:      '#e5e7eb',
      preBg:        '#1f2937',
      thBorders:    '#d1d5db',
      tdBorders:    '#e5e7eb',
    },
    dark: {
      body:         '#c8d8e0',
      headings:     '#f5f5f5',
      lead:         '#90aab8',
      links:        'var(--color-primary-400)',
      bold:         '#f5f5f5',
      counters:     '#9ca3af',
      bullets:      '#4b5563',
      hr:           '#374151',
      quotes:       '#f5f5f5',
      quoteBorders: 'var(--color-primary-500)',
      captions:     '#9ca3af',
      code:         '#f5f5f5',
      preCode:      '#e5e7eb',
      preBg:        '#030712',
      thBorders:    '#4b5563',
      tdBorders:    '#374151',
    },
  },
};

// ── applyTheme ──────────────────────────────────────────────────────────────
// Called by ContextApplier (on route + dark-mode change) and by the IIFE in
// DarkModeContext (on first load, before React renders).
theme.applyTheme = function applyTheme(isDark, isBlog) {
  const root  = document.documentElement;
  const ctx   = isBlog ? theme.blog : theme.app;
  const cfg   = ctx[isDark ? 'dark' : 'light'];
  const prose = theme.prose[isDark ? 'dark' : 'light'];

  // Background
  root.style.setProperty('--theme-bg-color', cfg.bgColor);
  root.style.setProperty(
    '--theme-bg-image',
    cfg.bgTexture && cfg.bgTexture.type ? cfg.bgTexture.value : 'none',
  );

  // Text colour
  root.style.setProperty('--theme-text-color', cfg.textColor);

  // Surface colours (cards, panels, inputs)
  root.style.setProperty('--surface-bg',     cfg.surfaceBg);
  root.style.setProperty('--surface-border', cfg.surfaceBorder);

  // Blog font (falls back to app font when blog.sans is null)
  const blogFont = (isBlog && theme.fonts.blog.sans) ? theme.fonts.blog.sans : theme.fonts.app.sans;
  root.style.setProperty('--theme-blog-font', blogFont);

  // Heading colour (null = fall through to textColor)
  root.style.setProperty('--theme-heading-color', cfg.headingColor || cfg.textColor);

  // Heading texture
  if (cfg.headingTexture) {
    root.classList.add('heading-textured');
    root.style.setProperty('--heading-texture', cfg.headingTexture);
  } else {
    root.classList.remove('heading-textured');
    root.style.removeProperty('--heading-texture');
  }

  // Brand color CSS vars — all shades injected so Tailwind classes (bg-primary-600 etc.)
  // resolve to runtime values. Changing colors.primary in theme.js updates the entire app.
  Object.entries(theme.colors.primary).forEach(([shade, value]) => {
    root.style.setProperty(`--color-primary-${shade}`, value);
  });
  Object.entries(theme.colors.secondary).forEach(([shade, value]) => {
    root.style.setProperty(`--color-secondary-${shade}`, value);
  });

  // Nav vars
  const nav = theme.nav[isDark ? 'dark' : 'light'];
  root.style.setProperty('--nav-bg',               nav.bg);
  root.style.setProperty('--nav-border',           nav.border);
  root.style.setProperty('--nav-brand-text',       nav.brandText);
  root.style.setProperty('--nav-link-text',        nav.linkText);
  root.style.setProperty('--nav-link-hover-text',  nav.linkHoverText);
  root.style.setProperty('--nav-link-hover-bg',    nav.linkHoverBg);
  root.style.setProperty('--nav-link-active-bg',   nav.linkActiveBg);
  root.style.setProperty('--nav-link-active-text', nav.linkActiveText);
  root.style.setProperty('--nav-dropdown-bg',      nav.dropdownBg);
  root.style.setProperty('--nav-dropdown-border',  nav.dropdownBorder);
  root.style.setProperty('--nav-dropdown-link',    nav.dropdownLinkText);
  root.style.setProperty('--nav-icon-text',        nav.iconText);
  root.style.setProperty('--nav-icon-hover-text',  nav.iconHoverText);
  root.style.setProperty('--nav-icon-hover-bg',    nav.iconHoverBg);
  root.style.setProperty('--nav-mobile-bg',        nav.mobileBg);
  root.style.setProperty('--nav-mobile-border',    nav.mobileBorder);

  // Shadows — static values exposed as CSS vars; shadow-clay-* classes in tailwind.config.js reference these
  const s = theme.shadows;
  root.style.setProperty('--shadow-clay',          s.clay);
  root.style.setProperty('--shadow-clay-hover',    s.clayHover);
  root.style.setProperty('--shadow-clay-btn',      s.clayBtn);
  root.style.setProperty('--shadow-clay-btn-press',s.clayBtnPress);
  root.style.setProperty('--shadow-clay-dark',     s.clayDark);
  root.style.setProperty('--shadow-clay-dark-hover',s.clayDarkHover);
  root.style.setProperty('--shadow-clay-btn-dark', s.clayBtnDark);

  // Prose CSS vars — convert camelCase keys to kebab-case var names
  Object.entries(prose).forEach(([key, value]) => {
    const varName = '--tw-prose-' + key.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
    root.style.setProperty(varName, value);
  });
};

module.exports = theme;
